/**
 * ImageUploader v3 — Upload + AI Enhance + AI Generate
 *
 * Props:
 *  aspectRatio   number    (default 1)
 *  onUploaded    fn(url)
 *  label         string
 *  currentUrl    string
 *  aiEnhance     bool      show AI tabs
 *  imageType     string    'food'|'banner'|'landing'|'logo'|'general'
 *  uploadContext object    { slug, branch, type, name }
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { BASE_URL } from '../api/baseURL';

const UPLOAD_URL      = `${BASE_URL}/api/upload`;
const AI_ENHANCE_URL  = `${BASE_URL}/api/ai-enhance`;
const AI_GENERATE_URL = `${BASE_URL}/api/ai-generate`;

// ── Style options shown as checkboxes ─────────────────────────────────────────
const STYLES = [
  { key: 'luxury',    emoji: '✨', label: 'Luxury'       },
  { key: 'warm',      emoji: '🔥', label: 'Warm & Cozy'  },
  { key: 'girly',     emoji: '🌸', label: 'Girly'        },
  { key: 'dark',      emoji: '🌑', label: 'Dark & Moody' },
  { key: 'bright',    emoji: '☀️', label: 'Bright & Airy' },
  { key: 'vibrant',   emoji: '🎨', label: 'Vibrant'      },
  { key: 'natural',   emoji: '🌿', label: 'Natural'      },
  { key: 'classic',   emoji: '🏛️', label: 'Classic'      },
];

// ── Canvas helpers ────────────────────────────────────────────────────────────
function canvasToBlob(canvas, type = 'image/jpeg', q = 0.82) {
  return new Promise(r => canvas.toBlob(r, type, q));
}
async function compressCanvas(canvas, maxKB) {
  for (const q of [0.88, 0.75, 0.60, 0.45, 0.30]) {
    const b = await canvasToBlob(canvas, 'image/jpeg', q);
    if (b.size <= maxKB * 1024) return b;
  }
  return canvasToBlob(canvas, 'image/jpeg', 0.25);
}
function loadImage(src) {
  return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
}
async function compressDataURI(uri, maxKB, maxDim = 1400) {
  const img = await loadImage(uri);
  const s = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(img.naturalWidth * s);
  c.height = Math.round(img.naturalHeight * s);
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
  const blob = await compressCanvas(c, maxKB);
  return new Promise(res => { const r = new FileReader(); r.onload = e => res(e.target.result); r.readAsDataURL(blob); });
}
async function dataURItoBlob(uri) {
  const [, b64] = uri.split(',');
  return new Blob([Uint8Array.from(atob(b64), c => c.charCodeAt(0))], { type: 'image/jpeg' });
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ImageUploader({
  aspectRatio = 1, onUploaded,
  label = 'Upload Image', currentUrl = '',
  aiEnhance = false,
  imageType = 'general',
  uploadContext = {},
}) {
  const fileInputRef = useRef(null);
  const canvasRef    = useRef(null);

  // tab: 'upload' | 'generate'
  const [tab,        setTab]        = useState('upload');
  const [stage,      setStage]      = useState('idle');
  const [imgSrc,     setImgSrc]     = useState(null);
  const [imgEl,      setImgEl]      = useState(null);
  const [error,      setError]      = useState('');
  const [previewUrl, setPreviewUrl] = useState(currentUrl);
  const [useAI,      setUseAI]      = useState(false);
  const [selStyles,  setSelStyles]  = useState([]);
  const [statusMsg,  setStatusMsg]  = useState('');

  // Generate tab state
  const [genPrompt,  setGenPrompt]  = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [genPreview, setGenPreview] = useState(null);

  // Crop state
  const [cropOrigin, setCropOrigin] = useState({ x: 0, y: 0 });
  const [scale,      setScale]      = useState(1);
  const dragging  = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0 });

  const toggleStyle = (key) =>
    setSelStyles(s => s.includes(key) ? s.filter(k => k !== key) : [...s, key]);

  // ── File pick ───────────────────────────────────────────────────────────────
  const pickFile = () => { setError(''); fileInputRef.current?.click(); };
  const onFileChange = (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/.test(file.type)) { setError('JPEG, PNG or WebP only'); return; }
    const r = new FileReader();
    r.onload = ev => { setImgSrc(ev.target.result); setStage('cropping'); };
    r.readAsDataURL(file);
  };

  // ── Image load + crop init ──────────────────────────────────────────────────
  useEffect(() => {
    if (!imgSrc) return;
    const img = new Image();
    img.onload = () => {
      setImgEl(img);
      const cw = Math.min(img.naturalWidth, img.naturalHeight * aspectRatio);
      const ch = cw / aspectRatio;
      setCropOrigin({ x: (img.naturalWidth - cw) / 2, y: (img.naturalHeight - ch) / 2 });
      setScale(1);
    };
    img.src = imgSrc;
  }, [imgSrc, aspectRatio]);

  // ── Canvas draw ─────────────────────────────────────────────────────────────
  useEffect(() => { if (stage === 'cropping' && imgEl && canvasRef.current) drawCanvas(); });
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas || !imgEl) return;
    const ctx = canvas.getContext('2d');
    const cw = canvas.width, ch = canvas.height;
    const cropW = imgEl.naturalWidth / scale, cropH = imgEl.naturalHeight / scale;
    const ox = Math.max(0, Math.min(cropOrigin.x, imgEl.naturalWidth - cropW));
    const oy = Math.max(0, Math.min(cropOrigin.y, imgEl.naturalHeight - cropH));
    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(imgEl, ox, oy, cropW, cropH, 0, 0, cw, ch);
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    const gx = cw*.05, gy = ch*.05;
    ctx.fillRect(0,0,cw,gy); ctx.fillRect(0,ch-gy,cw,gy);
    ctx.fillRect(0,gy,gx,ch*.9); ctx.fillRect(cw-gx,gy,gx,ch*.9);
    ctx.strokeStyle='rgba(255,255,255,.3)'; ctx.lineWidth=1;
    for(let i=1;i<3;i++){
      ctx.beginPath();ctx.moveTo(cw*i/3,0);ctx.lineTo(cw*i/3,ch);ctx.stroke();
      ctx.beginPath();ctx.moveTo(0,ch*i/3);ctx.lineTo(cw,ch*i/3);ctx.stroke();
    }
    ctx.strokeStyle = useAI ? '#a78bfa' : '#c8a96e';
    ctx.lineWidth = 2; ctx.strokeRect(1,1,cw-2,ch-2);
  }, [imgEl, cropOrigin, scale, useAI]);

  // ── Mouse/touch ─────────────────────────────────────────────────────────────
  const onMouseDown = e => { dragging.current=true; dragStart.current={mx:e.clientX,my:e.clientY,ox:cropOrigin.x,oy:cropOrigin.y}; };
  const onMouseMove = e => {
    if(!dragging.current||!imgEl||!canvasRef.current) return;
    const canvas=canvasRef.current;
    const cropW=imgEl.naturalWidth/scale, cropH=imgEl.naturalHeight/scale;
    const dx=(e.clientX-dragStart.current.mx)*(cropW/canvas.width);
    const dy=(e.clientY-dragStart.current.my)*(cropH/canvas.height);
    setCropOrigin({
      x:Math.max(0,Math.min(dragStart.current.ox-dx,imgEl.naturalWidth-cropW)),
      y:Math.max(0,Math.min(dragStart.current.oy-dy,imgEl.naturalHeight-cropH)),
    });
  };
  const onMouseUp = () => { dragging.current=false; };
  const onTouchStart = e => { const t=e.touches[0]; dragging.current=true; dragStart.current={mx:t.clientX,my:t.clientY,ox:cropOrigin.x,oy:cropOrigin.y}; };
  const onTouchMove  = e => onMouseMove({clientX:e.touches[0].clientX,clientY:e.touches[0].clientY});

  // ── Upload helper ───────────────────────────────────────────────────────────
  const uploadBlob = async (blob) => {
    const form = new FormData();
    form.append('image', blob, 'upload.jpg');
    // UPLOAD_URL is relative in production (same-origin), so give new URL a base.
    const url = new URL(UPLOAD_URL, window.location.origin);
    if (uploadContext.slug)   url.searchParams.set('slug',   uploadContext.slug);
    if (uploadContext.branch) url.searchParams.set('branch', uploadContext.branch);
    if (uploadContext.type)   url.searchParams.set('type',   uploadContext.type);
    if (uploadContext.name)   url.searchParams.set('name',   uploadContext.name);
    const resp = await fetch(url.toString(), { method: 'POST', body: form });
    const data = await resp.json();
    if (!data.success) throw new Error(data.message || 'Upload failed');
    return data.url;
  };

  // ── Confirm crop ────────────────────────────────────────────────────────────
  const confirmCrop = async () => {
    if (!imgEl) return;
    try {
      const cropW = imgEl.naturalWidth/scale, cropH = imgEl.naturalHeight/scale;
      const ox = Math.max(0,Math.min(cropOrigin.x,imgEl.naturalWidth-cropW));
      const oy = Math.max(0,Math.min(cropOrigin.y,imgEl.naturalHeight-cropH));
      const outW = Math.min(Math.round(cropW), aspectRatio>=1?1400:900);
      const outH = Math.round(outW/aspectRatio);
      const off = document.createElement('canvas');
      off.width=outW; off.height=outH;
      off.getContext('2d').drawImage(imgEl,ox,oy,cropW,cropH,0,0,outW,outH);

      let finalBlob;
      if (useAI) {
        setStage('enhancing'); setStatusMsg('Compressing for AI…');
        const preBlob = await compressCanvas(off, 800);
        const preURI  = await new Promise(res => { const r=new FileReader(); r.onload=e=>res(e.target.result); r.readAsDataURL(preBlob); });

        setStatusMsg('AI is enhancing your photo…');
        const aiResp = await fetch(AI_ENHANCE_URL, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ image: preURI, imageType, styles: selStyles }),
        });
        const aiData = await aiResp.json();
        if (!aiData.success) throw new Error(aiData.message || 'AI enhancement failed');

        setStatusMsg('Compressing result…');
        const compURI = await compressDataURI(aiData.image, 350, 1400);
        finalBlob = await dataURItoBlob(compURI);
        setStatusMsg('Uploading…');
        setStage('uploading');
      } else {
        setStage('uploading');
        finalBlob = await compressCanvas(off, 200);
      }

      const url = await uploadBlob(finalBlob);
      setPreviewUrl(url); onUploaded(url);
      setStage('done'); setImgSrc(null); setImgEl(null); setStatusMsg('');
    } catch (err) {
      setError(err.message); setStage('cropping'); setStatusMsg('');
    }
  };

  const cancelCrop = () => { setImgSrc(null); setImgEl(null); setStage('idle'); setStatusMsg(''); };

  // ── AI Generate (text-to-image) ─────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenLoading(true); setError('');
    try {
      const resp = await fetch(AI_GENERATE_URL, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          prompt: genPrompt,
          style: selStyles[0] || '',
          imageType,
          restaurantName: uploadContext.slug || '',
        }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.message);
      setGenPreview(data.image);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenLoading(false);
    }
  };

  const useGeneratedImage = async () => {
    if (!genPreview) return;
    setGenLoading(true); setStatusMsg('Compressing & uploading…');
    try {
      const compURI = await compressDataURI(genPreview, 350, 1600);
      const blob = await dataURItoBlob(compURI);
      const url = await uploadBlob(blob);
      setPreviewUrl(url); onUploaded(url);
      setGenPreview(null); setGenPrompt('');
    } catch (e) {
      setError(e.message);
    } finally {
      setGenLoading(false); setStatusMsg('');
    }
  };

  const canvasW = 480;
  const canvasH = Math.round(canvasW / aspectRatio);
  const isEnhancing  = stage === 'enhancing';
  const isUploading  = stage === 'uploading';
  const isProcessing = isEnhancing || isUploading;

  return (
    <>
      {/* ── Main trigger row ─────────────────────────────────────────────────── */}
      <div className="iu-row">
        {previewUrl && (
          <img src={previewUrl} alt="current" className="iu-thumb"
            style={{ aspectRatio:`${aspectRatio}/1` }}
            onError={e => e.target.style.display='none'} />
        )}
        <div className="iu-pick-col">
          {/* Tabs when AI is enabled */}
          {aiEnhance ? (
            <>
              <div className="iu-tabs">
                <button className={`iu-tab ${tab==='upload' ? 'active' : ''}`} onClick={() => setTab('upload')}>
                  📁 Upload
                </button>
                <button className={`iu-tab ${tab==='generate' ? 'active' : ''}`} onClick={() => setTab('generate')}>
                  ✦ Generate
                </button>
              </div>
              {tab === 'upload' && (
                <div className="iu-upload-row">
                  <button type="button" className="iu-pick-btn" onClick={pickFile}>{label}</button>
                  <button type="button" className={`iu-ai-toggle ${useAI ? 'active' : ''}`}
                    onClick={() => setUseAI(v => !v)}>
                    <span className="iu-ai-icon">✦</span> AI Enhance
                    <span className={`iu-ai-pill ${useAI?'on':'off'}`}>{useAI?'ON':'OFF'}</span>
                  </button>
                </div>
              )}
              {tab === 'generate' && (
                <div className="iu-gen-row">
                  <span className="iu-gen-label">✦ Describe what you want</span>
                  <input className="iu-gen-input"
                    placeholder={imageType==='banner' ? 'e.g. elegant outdoor terrace at sunset…' : 'e.g. warm cozy restaurant interior…'}
                    value={genPrompt}
                    onChange={e => setGenPrompt(e.target.value)} />
                </div>
              )}
            </>
          ) : (
            <button type="button" className="iu-pick-btn" onClick={pickFile}>📁 {label}</button>
          )}
        </div>
        {error && <span className="iu-inline-error">{error}</span>}
      </div>

      {/* ── Style checkboxes (shown when AI mode on or generate tab) ─────────── */}
      {aiEnhance && (useAI || tab==='generate') && (
        <div className="iu-styles-wrap">
          <span className="iu-styles-label">🎨 Style</span>
          <div className="iu-styles-grid">
            {STYLES.map(s => (
              <button key={s.key} type="button"
                className={`iu-style-btn ${selStyles.includes(s.key)?'active':''}`}
                onClick={() => toggleStyle(s.key)}>
                {s.emoji} {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Generate button ───────────────────────────────────────────────────── */}
      {aiEnhance && tab==='generate' && (
        <div style={{marginTop:'.5rem'}}>
          <button type="button" className="iu-gen-btn"
            onClick={handleGenerate} disabled={genLoading}>
            {genLoading ? '✦ Generating…' : '✦ Generate Image'}
          </button>
        </div>
      )}

      {/* ── Generated preview ─────────────────────────────────────────────────── */}
      {genPreview && (
        <div className="iu-gen-preview-wrap">
          <img src={genPreview} alt="generated" className="iu-gen-preview-img" />
          <div className="iu-gen-preview-actions">
            <button type="button" className="iu-gen-regen-btn" onClick={handleGenerate} disabled={genLoading}>↺ Regenerate</button>
            <button type="button" className="iu-gen-use-btn" onClick={useGeneratedImage} disabled={genLoading}>
              {genLoading ? 'Uploading…' : '✓ Use This Image'}
            </button>
          </div>
        </div>
      )}

      {/* AI hint */}
      {aiEnhance && tab==='upload' && useAI && stage==='idle' && (
        <div className="iu-ai-hint">
          ✦ AI will professionally retouch your photo
          {selStyles.length > 0 ? ` with a ${selStyles.join(' + ')} style` : ' — detecting best style automatically'}.
          Takes ~20 seconds.
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp"
        style={{display:'none'}} onChange={onFileChange} />

      {/* ── Crop modal ─────────────────────────────────────────────────────────── */}
      {stage==='cropping' && imgEl && (
        <div className="iu-modal-backdrop">
          <div className="iu-modal">
            <div className="iu-modal-header">
              <div>
                <h3>Crop Image</h3>
                <span className="iu-modal-hint">Drag to pan · Scroll to zoom</span>
              </div>
              {aiEnhance && (
                <button type="button" className={`iu-ai-toggle compact ${useAI?'active':''}`}
                  onClick={() => setUseAI(v=>!v)}>
                  <span className="iu-ai-icon">✦</span> AI Enhance
                  <span className={`iu-ai-pill ${useAI?'on':'off'}`}>{useAI?'ON':'OFF'}</span>
                </button>
              )}
            </div>

            {/* Style checkboxes in modal */}
            {aiEnhance && useAI && (
              <div className="iu-styles-wrap compact">
                <span className="iu-styles-label">Style</span>
                <div className="iu-styles-grid">
                  {STYLES.map(s => (
                    <button key={s.key} type="button"
                      className={`iu-style-btn ${selStyles.includes(s.key)?'active':''}`}
                      onClick={() => toggleStyle(s.key)}>
                      {s.emoji} {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="iu-canvas-wrap"
              style={{width:canvasW,maxWidth:'100%',aspectRatio:`${aspectRatio}/1`,cursor:'grab'}}
              onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
              onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onMouseUp}>
              <canvas ref={canvasRef} width={canvasW} height={canvasH}
                style={{width:'100%',height:'100%',display:'block',borderRadius:10}} />
            </div>

            <div className="iu-zoom-row">
              <span>🔍</span>
              <input type="range" min="1" max="4" step="0.05" value={scale}
                onChange={e => setScale(parseFloat(e.target.value))} className="iu-zoom-slider" />
              <span>{scale.toFixed(2)}×</span>
            </div>

            <div className="iu-modal-footer">
              <button className="iu-cancel-btn" onClick={cancelCrop}>Cancel</button>
              <button className={`iu-confirm-btn ${useAI?'ai':''}`} onClick={confirmCrop}>
                {useAI ? '✦ Crop & AI Enhance' : '✓ Crop & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Processing overlay ─────────────────────────────────────────────────── */}
      {isProcessing && (
        <div className="iu-modal-backdrop">
          <div className="iu-uploading-box">
            {isEnhancing ? (
              <>
                <div className="iu-ai-spinner">
                  <svg viewBox="0 0 60 60" width="60" height="60">
                    <circle cx="30" cy="30" r="26" fill="none" stroke="rgba(167,139,250,.15)" strokeWidth="3"/>
                    <circle cx="30" cy="30" r="26" fill="none" stroke="#a78bfa" strokeWidth="3"
                      strokeLinecap="round" strokeDasharray="40 122"
                      style={{transformOrigin:'30px 30px',animation:'iuAiSpin 1.2s linear infinite'}}/>
                  </svg>
                  <span className="iu-ai-star">✦</span>
                </div>
                <p className="iu-ai-status">{statusMsg || 'AI enhancing your image…'}</p>
                <p className="iu-ai-subtext">This takes 10–30 seconds.</p>
              </>
            ) : (
              <>
                <div className="iu-spinner"/>
                <p>{statusMsg || 'Compressing & uploading…'}</p>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .iu-row{display:flex;align-items:flex-start;gap:.75rem;flex-wrap:wrap;margin-top:.4rem}
        .iu-thumb{height:56px;border-radius:8px;border:1px solid rgba(255,255,255,.1);object-fit:cover}
        .iu-pick-col{display:flex;flex-direction:column;gap:.45rem;flex:1;min-width:180px}
        .iu-tabs{display:flex;border:1px solid rgba(255,255,255,.1);border-radius:10px;overflow:hidden;width:fit-content}
        .iu-tab{padding:.4rem .9rem;background:none;border:none;font-family:'DM Sans',sans-serif;font-size:.78rem;font-weight:600;color:rgba(255,255,255,.4);cursor:pointer;transition:all .2s}
        .iu-tab.active{background:rgba(255,255,255,.1);color:#fff}
        .iu-upload-row{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap}
        .iu-pick-btn{padding:.5rem 1rem;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.15);border-radius:10px;color:rgba(255,255,255,.8);font-family:'DM Sans',sans-serif;font-size:.82rem;cursor:pointer;white-space:nowrap;transition:all .2s}
        .iu-pick-btn:hover{background:rgba(255,255,255,.14);color:#fff}
        .iu-ai-toggle{display:inline-flex;align-items:center;gap:.4rem;padding:.4rem .8rem;border-radius:10px;border:1px solid rgba(167,139,250,.2);background:rgba(167,139,250,.06);color:rgba(167,139,250,.7);font-family:'DM Sans',sans-serif;font-size:.78rem;font-weight:600;cursor:pointer;transition:all .2s;white-space:nowrap}
        .iu-ai-toggle.active{border-color:rgba(167,139,250,.55);background:rgba(167,139,250,.16);color:#c4b5fd;box-shadow:0 0 12px rgba(167,139,250,.2)}
        .iu-ai-toggle.compact{font-size:.72rem;padding:.32rem .65rem}
        .iu-ai-icon{font-size:.85rem}
        .iu-ai-pill{padding:.08rem .35rem;border-radius:5px;font-size:.62rem;font-weight:800}
        .iu-ai-pill.on{background:rgba(167,139,250,.25);color:#e9d5ff}
        .iu-ai-pill.off{background:rgba(255,255,255,.07);color:rgba(255,255,255,.3)}
        .iu-inline-error{font-size:.78rem;color:#fca5a5;align-self:center}
        .iu-ai-hint{margin-top:.3rem;padding:.5rem .8rem;background:rgba(167,139,250,.08);border:1px solid rgba(167,139,250,.15);border-radius:10px;font-size:.75rem;color:rgba(167,139,250,.8);line-height:1.5}

        /* Styles grid */
        .iu-styles-wrap{display:flex;align-items:flex-start;gap:.5rem;margin-top:.5rem;flex-wrap:wrap}
        .iu-styles-wrap.compact{margin-top:.25rem}
        .iu-styles-label{font-size:.72rem;color:rgba(255,255,255,.35);white-space:nowrap;padding-top:.25rem;font-weight:600}
        .iu-styles-grid{display:flex;flex-wrap:wrap;gap:.3rem}
        .iu-style-btn{padding:.28rem .65rem;border-radius:8px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);color:rgba(255,255,255,.5);font-family:'DM Sans',sans-serif;font-size:.72rem;cursor:pointer;transition:all .2s;white-space:nowrap}
        .iu-style-btn:hover{border-color:rgba(255,255,255,.18);color:rgba(255,255,255,.8)}
        .iu-style-btn.active{border-color:rgba(167,139,250,.5);background:rgba(167,139,250,.15);color:#c4b5fd}

        /* Generate tab */
        .iu-gen-row{display:flex;flex-direction:column;gap:.3rem}
        .iu-gen-label{font-size:.72rem;color:rgba(255,255,255,.4);font-weight:600}
        .iu-gen-input{padding:.5rem .75rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.82rem;width:100%;box-sizing:border-box}
        .iu-gen-input:focus{outline:none;border-color:rgba(167,139,250,.4)}
        .iu-gen-btn{padding:.55rem 1.25rem;background:linear-gradient(135deg,#7c3aed,#a78bfa);border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.82rem;font-weight:700;cursor:pointer;transition:opacity .2s;box-shadow:0 4px 16px rgba(139,92,246,.3)}
        .iu-gen-btn:hover{opacity:.88}
        .iu-gen-btn:disabled{opacity:.5;cursor:wait}
        .iu-gen-preview-wrap{margin-top:.75rem;display:flex;flex-direction:column;gap:.5rem}
        .iu-gen-preview-img{width:100%;border-radius:10px;border:1px solid rgba(167,139,250,.2);object-fit:cover;max-height:220px}
        .iu-gen-preview-actions{display:flex;gap:.5rem}
        .iu-gen-regen-btn{flex:1;padding:.5rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);border-radius:8px;color:rgba(255,255,255,.6);font-family:'DM Sans',sans-serif;font-size:.78rem;cursor:pointer}
        .iu-gen-use-btn{flex:2;padding:.5rem;background:#c8a96e;border:none;border-radius:8px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.82rem;font-weight:700;cursor:pointer}
        .iu-gen-use-btn:disabled{opacity:.5}

        /* Modal */
        .iu-modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.82);backdrop-filter:blur(14px);z-index:2000;display:flex;align-items:center;justify-content:center;padding:1rem}
        .iu-modal{background:#1a1a1a;border:1px solid rgba(255,255,255,.12);border-radius:20px;padding:1.5rem;width:100%;max-width:520px;display:flex;flex-direction:column;gap:1rem;max-height:90vh;overflow-y:auto}
        .iu-modal-header{display:flex;align-items:center;justify-content:space-between;gap:1rem;flex-wrap:wrap}
        .iu-modal-header h3{font-family:'Playfair Display',serif;color:#fff;font-size:1.1rem;margin:0 0 .1rem}
        .iu-modal-hint{font-size:.72rem;color:rgba(255,255,255,.3)}
        .iu-canvas-wrap{border-radius:10px;overflow:hidden;border:2px solid rgba(255,255,255,.12);user-select:none}
        .iu-zoom-row{display:flex;align-items:center;gap:.7rem;font-size:.8rem;color:rgba(255,255,255,.45)}
        .iu-zoom-slider{flex:1;accent-color:#c8a96e;cursor:pointer}
        .iu-modal-footer{display:flex;gap:.7rem;justify-content:flex-end}
        .iu-cancel-btn{padding:.65rem 1.3rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:10px;color:rgba(255,255,255,.55);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.88rem}
        .iu-confirm-btn{padding:.65rem 1.6rem;background:#c8a96e;border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.88rem;font-weight:700;cursor:pointer}
        .iu-confirm-btn.ai{background:linear-gradient(135deg,#7c3aed,#a78bfa);box-shadow:0 4px 18px rgba(139,92,246,.35)}

        /* Processing */
        .iu-uploading-box{background:#1a1a1a;border-radius:24px;padding:3rem 2.5rem;display:flex;flex-direction:column;align-items:center;gap:1rem;color:rgba(255,255,255,.6);font-family:'DM Sans',sans-serif;text-align:center;min-width:260px}
        .iu-spinner{width:38px;height:38px;border:3px solid rgba(255,255,255,.1);border-top-color:#c8a96e;border-radius:50%;animation:iuSpin .8s linear infinite}
        @keyframes iuSpin{to{transform:rotate(360deg)}}
        .iu-ai-spinner{position:relative;width:60px;height:60px}
        .iu-ai-star{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:1.4rem;color:#a78bfa;animation:iuPulse 2s ease-in-out infinite}
        @keyframes iuAiSpin{to{transform:rotate(360deg)}}
        @keyframes iuPulse{0%,100%{opacity:.5;transform:scale(1)}50%{opacity:1;transform:scale(1.2)}}
        .iu-ai-status{font-size:.92rem;font-weight:600;color:#c4b5fd;margin:0}
        .iu-ai-subtext{font-size:.75rem;color:rgba(255,255,255,.3);margin:0}
      `}</style>
    </>
  );
}
